import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Package, Search } from "lucide-react";
import BackButton from "@/components/BackButton";
import BulkUploadDialog from "@/components/BulkUploadDialog";

export default function FinishedProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(blank());

  function blank() { return { name: "", code: "", size: "", design_name: "", finish: "", hsn_code: "", unit: "box", sqm_per_unit: 0, sale_price: 0, tax_percent: 18, tds_percent: 0, description: "" }; }

  const load = async () => {
    const { data } = await api.get("/org/finished-products");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.code) { toast.error("Name & code are required"); return; }
    try {
      const payload = {
        ...form,
        sqm_per_unit: Number(form.sqm_per_unit || 0),
        sale_price: Number(form.sale_price || 0),
        tax_percent: Number(form.tax_percent || 0),
        tds_percent: Number(form.tds_percent || 0),
      };
      if (editing) {
        await api.patch(`/org/finished-products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/org/finished-products", payload);
        toast.success("Product created");
      }
      setOpen(false); setForm(blank()); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const edit = (p) => {
    setForm({
      name: p.name || "", code: p.code || "", size: p.size || "", design_name: p.design_name || "",
      finish: p.finish || "", hsn_code: p.hsn_code || "", unit: p.unit || "box",
      sqm_per_unit: p.sqm_per_unit || 0, sale_price: p.sale_price || 0,
      tax_percent: p.tax_percent ?? 18, tds_percent: p.tds_percent || 0,
      description: p.description || "",
    });
    setEditing(p.id); setOpen(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/org/finished-products/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = items.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) || (p.code || "").toLowerCase().includes(s) || (p.design_name || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="finished-products-page">
      <BackButton />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-3"><Package className="w-7 h-7" /> Finished Products</h1>
          <p className="text-muted-foreground">Master list of finished tiles / SKUs. Used across Sales, Production, QC, Dispatch and Ledger.</p>
        </div>
        <div className="flex items-center gap-2">
          <BulkUploadDialog
            title="Bulk upload finished products"
            templateUrl="/org/finished-products/template"
            uploadUrl="/org/finished-products/bulk"
            payloadKey="products"
            testId="bulk-fp"
            onSuccess={load}
          />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank()); } }}>
            <DialogTrigger asChild><Button data-testid="new-finished-product-btn"><Plus className="w-4 h-4 mr-1" /> New product</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="finished-product-dialog">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} finished product</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name*</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fp-name" /></div>
              <div><Label>Code / SKU*</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="fp-code" /></div>
              <div><Label>Size</Label><Input placeholder="e.g. 600x600" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} data-testid="fp-size" /></div>
              <div><Label>Design name</Label><Input value={form.design_name} onChange={(e) => setForm({ ...form, design_name: e.target.value })} data-testid="fp-design" /></div>
              <div><Label>Finish</Label><Input placeholder="Glossy / Matt / Polished" value={form.finish} onChange={(e) => setForm({ ...form, finish: e.target.value })} data-testid="fp-finish" /></div>
              <div><Label>HSN code</Label><Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} data-testid="fp-hsn" /></div>
              <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="fp-unit" /></div>
              <div><Label>SQM per unit</Label><Input type="number" step="0.01" value={form.sqm_per_unit} onChange={(e) => setForm({ ...form, sqm_per_unit: e.target.value })} data-testid="fp-sqm" /></div>
              <div><Label>Sale price (₹)</Label><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="fp-price" /></div>
              <div><Label>GST %</Label><Input type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} data-testid="fp-tax" /></div>
              <div><Label>TDS %</Label><Input type="number" step="0.01" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} data-testid="fp-tds" /></div>
              <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="fp-desc" /></div>
            </div>
            <Button onClick={submit} data-testid="fp-save-btn">{editing ? "Update" : "Create"} product</Button>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="surface-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name, code, design…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="fp-search" className="max-w-sm" />
          <span className="text-xs text-muted-foreground">{filtered.length} product{filtered.length === 1 ? "" : "s"}</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Size</TableHead>
                <TableHead>Design</TableHead><TableHead>Finish</TableHead><TableHead>HSN</TableHead>
                <TableHead>Unit</TableHead><TableHead>Sale ₹</TableHead>
                <TableHead>GST%</TableHead><TableHead>TDS%</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`fp-row-${p.id}`}>
                  <TableCell className="font-mono text-xs">{p.code}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.size || "—"}</TableCell>
                  <TableCell>{p.design_name || "—"}</TableCell>
                  <TableCell>{p.finish || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{p.hsn_code || "—"}</TableCell>
                  <TableCell>{p.unit}</TableCell>
                  <TableCell className="font-mono">₹{Number(p.sale_price || 0).toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-xs">{p.tax_percent ?? 0}</TableCell>
                  <TableCell className="font-mono text-xs">{p.tds_percent ?? 0}</TableCell>
                  <TableCell className="flex gap-2">
                    <button onClick={() => edit(p)} data-testid={`fp-edit-${p.id}`} className="text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p.id)} data-testid={`fp-delete-${p.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No finished products yet. Add one to enable dropdowns across Sales, Production, QC and Dispatch.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
